import {
    DndContext,
    DragOverlay,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {getPaginatedData} from "../../../api/duckdb/getPaginatedData";
import {getQueryResults} from "../../../api/duckdb/getQueryResults";
import InfoBox from "../info-box/InfoBox";
import LoadingComponent from "../../loading/LoadingComponent";
import AsyncTableBody from "./AsyncTableBody";
import AsyncTableHeaders from "./AsyncTableHeaders";
import AsyncTableLoader from "./AsyncTableLoader";
import AsyncTablePagination from "./AsyncTablePagination";
import "./_async-table.scss";
import AsyncTableFooter from "./AsyncTableFooter";
import AsyncTableColGroups from "./AsyncTableColGroups";
import {useTranslation} from "react-i18next";
import AsyncTableActionBar from "./AsyncTableActionBar";
import AsyncTableGroupedBody from "./AsyncTableGroupedBody";
import AsyncTableGroupRuleModal from "./AsyncTableGroupRuleModal";
import {
    buildGroupLevel,
    buildGroupSort,
    DEFAULT_DATE_UNIT,
    normalizeDateUnit,
} from "./asyncTableGroupRules";
import {
    buildAggregations,
    buildFilter,
    MAX_GROUP_LEVELS,
} from "./asyncTableGrouping";
import useAsyncTableGroups from "./useAsyncTableGroups";
import useTableViews from "./useTableViews";
import AsyncTableSettings from "./AsyncTableSettings";
import {
    applyAggregates,
    applyColumnOrder,
    applyLayout,
    filterHiddenHeaders,
    findColumn,
    getDefaultHiddenKeys,
    buildGroupLevelHeaders,
    normalizeGroupBy,
    layoutAddGroup,
    layoutFromHeaders,
    layoutMoveColumn,
    layoutMoveGroup,
    layoutRemoveGroup,
    layoutRenameGroup,
    withColumnKeys,
} from "./asyncTableColumns";

const EMPTY_ORDER = [];

const EMPTY_AGGREGATES = {};

const EMPTY_GROUP_RULES = {};

const EMPTY_GROUP_SORTS = {};

const GROUP_DROPZONE_ID = "async-table-group-by";

const DEFAULT_GROUP_ROWS_LIMIT = 10;

const SCROLLABLE_OVERFLOW = /(auto|scroll|overlay)/;

/**
 * Kontener przewijany przy przeciąganiu nagłówka: najdalszy przodek, który naprawdę przewija się w pionie
 * (w modalu - modal, na stronie - jej kontener), inaczej dokument; w modalu, który się nie przewija - brak.
 * dnd-kit bierze przewijane elementy z przodków elementu pod kursorem (pasek grupowania i kontener tabeli
 * mają overflow-x), a strefy przewijania liczy od ich pełnych wymiarów - bez zawężenia strefy pojawiały się
 * i znikały w trakcie ruchu.
 * @param {Element} [element] przeciągany nagłówek
 * @returns {Element|null}
 */
function findDragScrollContainer(element) {
    let result = null;
    for (
        let node = element?.parentElement;
        node && node !== document.body;
        node = node.parentElement
    ) {
        const {overflowY} = window.getComputedStyle(node);
        if (
            SCROLLABLE_OVERFLOW.test(overflowY) &&
            node.scrollHeight > node.clientHeight
        ) {
            result = node;
        }
    }
    if (result) return result;
    // modal bez przewijania - strona pod nim nie może się przesuwać
    return element?.closest(".modal") ? null : document.scrollingElement;
}

/**
 * Kolejność grup poziomu w ustawieniach; brak wpisu - domyślna (rosnąco).
 * @returns {Object} nowe groupSorts
 */
function withGroupSort(settings, columnKey, sort) {
    const {[columnKey]: previous, ...groupSorts} = settings.groupSorts ?? {};
    return sort ? {...groupSorts, [columnKey]: sort} : groupSorts;
}

/**
 * Komponent do tworzenia asyncrhonicznej tabeli.
 * Możesz przekazać query do wykonania oraz parametry do tego query lub funkcje do pobierania danych.
 * Kolumny można ukrywać, przestawiać (w obrębie swojej grupy nagłówków) i zmieniać im agregat w stopce.
 * Nagłówek kolumny można przeciągnąć na strefę "Grupuj według" - kolejne kolumny to kolejne poziomy
 * Ustawienia trzymane są tylko w pamięci; trwale zapisuje je zapisany widok (settings-api, prop `tableId`).
 * Nagłówek z `hidden: true` jest domyślnie ukryty.
 * Nagłówek z formatterem zmieniającym jednostkę (np. waga g -> kg) podaje `inputConverter: {toRaw, fromRaw}` -
 * liczby w wyrażeniach grupowania wpisuje się w jednostce wyświetlania (np. getWeightInputConverter).
 * @param {*} param0
 * @returns
 */
export default function AsyncTable({
    query,
    params,
    headers,
    shouldIndex,
    fetchInitialData,
    setResultID,
    tableId,
}) {
    const table = useRef();

    const {t} = useTranslation();

    const [data, setData] = useState(new Map());
    const [limit, setLimit] = useState(25);
    const [page, setPage] = useState(1);
    const [allItems, setAllItems] = useState(0);
    const [error, setError] = useState(false);
    const [id, setId] = useState("");
    const [loading, setLoading] = useState(false);
    const [sort, setSort] = useState([]);
    const [filters, setFilters] = useState({});
    const [showSettings, setShowSettings] = useState(false);
    // {hidden?: string[], order?: string[], aggregates?: {[columnKey]: string}, groupBy?: string[],
    // groupRules?: {[columnKey]: {expression, names, rest, nullGroup}},
    // groupSorts?: {[columnKey]: "asc"|"desc"|"count"}}
    // brak pola oznacza ustawienia domyślne
    const [userSettings, setUserSettings] = useState({});

    useEffect(() => {
        if (!query && !fetchInitialData) return;
        // wczytywanie danych poczatkowych
        async function getInitialData() {
            try {
                setLoading(true);
                setFilters({});
                if (fetchInitialData) {
                    const res = await fetchInitialData(limit);
                    if (res.ExecCode === "FAIL") {
                        setError(true);
                    } else {
                        const data = new Map();
                        data.set(1, res.Data);
                        setData(data);
                        setAllItems(res.ResultRowsCnt);
                        setId(res.ResultID);
                    }
                } else {
                    const res = await getQueryResults({
                        Queries: [{SQL: query, Parameters: params}],
                        ToTempFilesRowsThreshold: limit,
                        ForceTempTable: true,
                    });
                    const queryResult = res.QueriesResults[0];
                    if (queryResult.ExecCode === "FAIL") {
                        setError(true);
                    } else {
                        const data = new Map();
                        data.set(1, queryResult.Data);
                        setData(data);
                        setAllItems(queryResult.ResultRowsCnt);
                        setId(queryResult.ResultID);
                    }
                }
            } catch (e) {
                console.error(e);
                setError(true);
            } finally {
                setLoading(false);
            }
        }

        setPage(1);
        setAllItems(0);
        setId("");

        getInitialData();
    }, [query, fetchInitialData, params]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (setResultID) setResultID(id);
    }, [id, setResultID]);

    const getPageData = useCallback(
        async (page, limit, sort, filters) => {
            if (!id) return;
            const tmpFilters = buildFilter(filters);
            console.log("[AsyncTable] Filters", tmpFilters);
            try {
                setLoading(true);
                const res = await getPaginatedData({
                    ResultID: id,
                    StartOffset: (page - 1) * limit,
                    RecordsCount: limit,
                    SortFields: sort,
                    Filter: tmpFilters,
                });
                setData((prev) => {
                    const clone = new Map(prev);
                    clone.set(page, res.Data);
                    return clone;
                });
                if (res.AllRecordsCount !== undefined) {
                    setAllItems(res.AllRecordsCount);
                }
            } catch (e) {
                console.error(e);
                setError(true);
            } finally {
                setLoading(false);
            }
        },
        [id]
    );

    const dataToRender = useMemo(() => {
        return data.get(page) || [];
    }, [data, page]);

    const keyedHeaders = useMemo(() => withColumnKeys(headers), [headers]);

    const defaultHiddenKeys = useMemo(
        () => getDefaultHiddenKeys(keyedHeaders),
        [keyedHeaders]
    );

    const hiddenKeys = userSettings.hidden ?? defaultHiddenKeys;

    const columnOrder = userSettings.order ?? EMPTY_ORDER;

    const aggregates = userSettings.aggregates ?? EMPTY_AGGREGATES;

    const updateSettings = useCallback((update) => {
        setUserSettings((prev) => update(prev));
    }, []);

    const setHiddenKeys = useCallback(
        (next) => {
            updateSettings((prev) => ({
                ...prev,
                hidden:
                    typeof next === "function"
                        ? next(prev.hidden ?? defaultHiddenKeys)
                        : next,
            }));
        },
        [updateSettings, defaultHiddenKeys]
    );

    const orderedHeaders = useMemo(
        () =>
            userSettings.layout
                ? applyLayout(keyedHeaders, userSettings.layout)
                : applyColumnOrder(keyedHeaders, columnOrder),
        [keyedHeaders, columnOrder, userSettings.layout]
    );

    // układ kolumn użytkownika (kolejność i grupy kolumn) - przy pierwszej zmianie tworzony z bieżących nagłówków
    const updateLayout = useCallback(
        (update) => {
            updateSettings((prev) => {
                const layout =
                    prev.layout ??
                    layoutFromHeaders(
                        applyColumnOrder(
                            keyedHeaders,
                            prev.order ?? EMPTY_ORDER
                        )
                    );
                const next = update(layout);
                if (next === layout && prev.layout) return prev;
                return {...prev, layout: next};
            });
        },
        [updateSettings, keyedHeaders]
    );

    /**
     * Przeniesienie w ustawieniach: grupa przenosi się w całości, kolumna może zmienić grupę.
     * @param {string} activeKey przeciągany element
     * @param {string} overKey element docelowy
     * @param {{into?: boolean, toRoot?: boolean}} [options] na koniec grupy / na poziom grup
     */
    const onMoveColumn = useCallback(
        (activeKey, overKey, options) => {
            updateLayout((layout) =>
                layout.some((item) => item.group === activeKey)
                    ? layoutMoveGroup(layout, activeKey, overKey)
                    : layoutMoveColumn(layout, activeKey, overKey, options)
            );
        },
        [updateLayout]
    );

    // nowa, pusta grupa kolumn - zwraca jej id (szuflada od razu otwiera edycję nazwy)
    const onAddColumnGroup = useCallback(
        (name) => {
            const id = `custom-${Date.now()}`;
            updateLayout((layout) => layoutAddGroup(layout, id, name));
            return id;
        },
        [updateLayout]
    );

    const onRenameColumnGroup = useCallback(
        (id, name) =>
            updateLayout((layout) => layoutRenameGroup(layout, id, name)),
        [updateLayout]
    );

    const onRemoveColumnGroup = useCallback(
        (id) => updateLayout((layout) => layoutRemoveGroup(layout, id)),
        [updateLayout]
    );

    const onAggregateChange = useCallback(
        (columnKey, aggregate) => {
            updateSettings((prev) => ({
                ...prev,
                aggregates: {...prev.aggregates, [columnKey]: aggregate},
            }));
        },
        [updateSettings]
    );

    // zapisane widoki (settings-api) - domyślny widok to ustawienia z propsów tabeli
    const applyViewSettings = useCallback(
        (settings) => updateSettings(() => ({...settings})),
        [updateSettings]
    );

    const views = useTableViews({
        tableId,
        settings: userSettings,
        applySettings: applyViewSettings,
    });

    const onSettingsClick = useCallback(() => setShowSettings(true), []);

    const onSettingsHide = useCallback(() => setShowSettings(false), []);

    const groupColumns = useMemo(
        () =>
            normalizeGroupBy(userSettings.groupBy)
                .map((columnKey) => findColumn(keyedHeaders, columnKey))
                .filter(Boolean),
        [keyedHeaders, userSettings.groupBy]
    );

    // kolejna kolumna to kolejny poziom grupowania (maks. MAX_GROUP_LEVELS); kolumna już użyta jest pomijana
    const onGroupBy = useCallback(
        (columnKey) => {
            updateSettings((prev) => {
                const groupBy = normalizeGroupBy(prev.groupBy);
                if (
                    groupBy.includes(columnKey) ||
                    groupBy.length >= MAX_GROUP_LEVELS
                ) {
                    return prev;
                }
                return {...prev, groupBy: [...groupBy, columnKey]};
            });
        },
        [updateSettings]
    );

    // zmiana kolejności poziomów grupowania (lista poziomów w ustawieniach)
    const onMoveGroupLevel = useCallback(
        (activeKey, overKey) => {
            updateSettings((prev) => {
                const groupBy = normalizeGroupBy(prev.groupBy).slice();
                const from = groupBy.indexOf(activeKey);
                const to = groupBy.indexOf(overKey);
                if (from === -1 || to === -1 || from === to) return prev;
                groupBy.splice(to, 0, groupBy.splice(from, 1)[0]);
                return {...prev, groupBy};
            });
        },
        [updateSettings]
    );

    // usunięcie poziomu usuwa też jego regułę - ponowne grupowanie po kolumnie zaczyna od każdej wartości
    const onRemoveGroup = useCallback(
        (columnKey) => {
            updateSettings((prev) => {
                const next = {
                    ...prev,
                    groupBy: normalizeGroupBy(prev.groupBy).filter(
                        (key) => key !== columnKey
                    ),
                };
                if (prev.groupRules?.[columnKey]) {
                    const {[columnKey]: removed, ...groupRules} =
                        prev.groupRules;
                    next.groupRules = groupRules;
                }
                if (prev.groupSorts?.[columnKey]) {
                    const {[columnKey]: removed, ...groupSorts} =
                        prev.groupSorts;
                    next.groupSorts = groupSorts;
                }
                if (prev.groupDateUnits?.[columnKey]) {
                    const {[columnKey]: removed, ...groupDateUnits} =
                        prev.groupDateUnits;
                    next.groupDateUnits = groupDateUnits;
                }
                return next;
            });
        },
        [updateSettings]
    );

    const groupRules = userSettings.groupRules ?? EMPTY_GROUP_RULES;

    const groupSorts = userSettings.groupSorts ?? EMPTY_GROUP_SORTS;

    // okres grupowania kolumn z datą po columnKey (brak - dzień)
    const groupDateUnits = userSettings.groupDateUnits ?? EMPTY_GROUP_SORTS;

    // poziom grupowania otwarty w modalu reguły (columnKey)
    const [ruleColumnKey, setRuleColumnKey] = useState(null);

    const onCloseGroupRule = useCallback(() => setRuleColumnKey(null), []);

    // kolejność grup poziomu "Każda wartość" (modal albo kliknięcie nagłówka kolumny poziomu)
    const onGroupSortChange = useCallback(
        (columnKey, sort) =>
            updateSettings((prev) => ({
                ...prev,
                groupSorts: withGroupSort(prev, columnKey, sort),
            })),
        [updateSettings]
    );

    // rule null - "Każda wartość" (reguła usunięta); sort - kolejność grup przy "Każdej wartości";
    // dateUnit - okres kolumny z datą (domyślny dzień nie jest zapisywany)
    const onApplyGroupRule = useCallback(
        (rule, sort, dateUnit) => {
            const columnKey = ruleColumnKey;
            setRuleColumnKey(null);
            updateSettings((prev) => {
                const {[columnKey]: previous, ...rest} = prev.groupRules ?? {};
                const {[columnKey]: previousUnit, ...units} =
                    prev.groupDateUnits ?? {};
                const unit = normalizeDateUnit(dateUnit);
                return {
                    ...prev,
                    groupRules: rule ? {...rest, [columnKey]: rule} : rest,
                    groupSorts: withGroupSort(prev, columnKey, sort),
                    groupDateUnits:
                        unit === DEFAULT_DATE_UNIT
                            ? units
                            : {...units, [columnKey]: unit},
                };
            });
        },
        [ruleColumnKey, updateSettings]
    );

    // przesuniecie o kilka px zanim ruszy przeciaganie - zwykle klikniecie w naglowek dalej sortuje
    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
        useSensor(TouchSensor, {
            activationConstraint: {delay: 250, tolerance: 5},
        })
    );

    const [draggedColumn, setDraggedColumn] = useState(null);

    // jedyny kontener przewijany w trakcie przeciągania - wybierany na starcie
    const dragScrollContainer = useRef(null);

    const onDragStart = useCallback(({active, activatorEvent}) => {
        dragScrollContainer.current = findDragScrollContainer(
            activatorEvent?.target
        );
        setDraggedColumn(active.data.current?.column ?? null);
    }, []);

    // tylko w pionie, przy samej krawędzi i wolniej niż domyślnie
    const autoScroll = useMemo(
        () => ({
            threshold: {x: 0, y: 0.1},
            acceleration: 5,
            canScroll: (element) => element === dragScrollContainer.current,
        }),
        []
    );

    const onDragEnd = useCallback(
        ({active, over}) => {
            setDraggedColumn(null);
            if (over?.id !== GROUP_DROPZONE_ID) return;
            onGroupBy(active.id);
        },
        [onGroupBy]
    );

    const onDragCancel = useCallback(() => setDraggedColumn(null), []);

    // domyślny widok: ustawienia z propsów tabeli, bez aktywnego zapisanego widoku
    const {clearActive: clearActiveView} = views;
    const onRestoreDefault = useCallback(() => {
        updateSettings(() => ({}));
        clearActiveView();
    }, [updateSettings, clearActiveView]);

    // poziomy z poprawną regułą - wartości w grupie się różnią, więc w wierszach danych nie są przygaszone
    const ruleLevelKeys = useMemo(
        () =>
            groupColumns
                .filter(
                    (column) =>
                        !!buildGroupLevel(
                            column.filterColumn || column.field,
                            column.filterType,
                            groupRules[column.columnKey]
                        )?.Buckets
                )
                .map((column) => column.columnKey),
        [groupColumns, groupRules]
    );

    // agregaty podmieniamy tylko dla tabeli - szuflada potrzebuje domyślnych, żeby wyliczyć dostępne opcje
    // w trybie grupowania kolumny poziomów grupowania stoją na początku, każda z własnym nagłówkiem i filtrem
    const visibleHeaders = useMemo(
        () =>
            buildGroupLevelHeaders(
                filterHiddenHeaders(
                    applyAggregates(orderedHeaders, aggregates),
                    hiddenKeys
                ),
                groupColumns,
                ruleLevelKeys
            ),
        [orderedHeaders, aggregates, hiddenKeys, groupColumns, ruleLevelKeys]
    );

    // zmiana zestawu lub kolejnosci kolumn wymaga przeliczenia szerokosci w colgroup, wiec montujemy tabele od nowa
    const tableKey = useMemo(
        () =>
            `${hiddenKeys.join("|")}/${columnOrder.join("|")}/${normalizeGroupBy(
                userSettings.groupBy
            ).join("|")}/${JSON.stringify(userSettings.layout ?? null)}`,
        [hiddenKeys, columnOrder, userSettings.groupBy, userSettings.layout]
    );

    const flattenedHeaders = useMemo(() => {
        const newHeaders = [];

        const flattenHeaders = (header) => {
            if (header.children) {
                header.children.forEach(flattenHeaders);
            } else {
                newHeaders.push(header);
            }
        };

        visibleHeaders.forEach(flattenHeaders);

        return newHeaders;
    }, [visibleHeaders]);

    const isGrouped = groupColumns.length > 0;

    // numeracja wierszy w grupie (1, 1, 1...) nic nie mówi i rozdziela kolumny poziomów od danych
    const showIndex = shouldIndex && !isGrouped;

    // pola grupowania w API - te same nazwy co w filtrach i sortowaniu
    const groupFields = useMemo(
        () => groupColumns.map((column) => column.filterColumn || column.field),
        [groupColumns]
    );

    // naglowki poziomow po polu grupowania - do formatowania kluczy grup
    const groupHeaders = useMemo(
        () =>
            Object.fromEntries(
                groupColumns.map((column) => [
                    column.filterColumn || column.field,
                    column,
                ])
            ),
        [groupColumns]
    );

    // poziomy w GroupFields API: pole, pole z przedziałami reguły (klucz grupy = indeks przedziału)
    // albo kolumna z datą z okresem (DateUnit)
    const apiGroupFields = useMemo(
        () =>
            groupColumns.map((column) =>
                buildGroupLevel(
                    column.filterColumn || column.field,
                    column.filterType,
                    groupRules[column.columnKey],
                    groupDateUnits[column.columnKey],
                    column.inputConverter
                )
            ),
        [groupColumns, groupRules, groupDateUnits]
    );

    // okres poziomów z datą po polu API - etykiety grup (tydzień, miesiąc)
    const groupDateUnitsByField = useMemo(() => {
        const result = {};
        apiGroupFields.forEach((level) => {
            if (level?.DateUnit) result[level.field] = level.DateUnit;
        });
        return result;
    }, [apiGroupFields]);

    // reguły poziomów po polu API - etykiety grup; tylko poprawne reguły (błędna = każda wartość)
    const groupRulesByField = useMemo(() => {
        const result = {};
        groupColumns.forEach((column, index) => {
            if (!apiGroupFields[index]?.Buckets) return;
            result[column.filterColumn || column.field] = {
                rule: groupRules[column.columnKey],
                filterType: column.filterType,
            };
        });
        return result;
    }, [groupColumns, apiGroupFields, groupRules]);

    // kolejność grup każdego poziomu; poziom z regułą ma kolejność wyrażeń (indeks przedziału)
    const levelGroupSorts = useMemo(
        () =>
            groupColumns.map((column, index) =>
                apiGroupFields[index]?.Buckets
                    ? undefined
                    : buildGroupSort(groupSorts[column.columnKey], column)
            ),
        [groupColumns, apiGroupFields, groupSorts]
    );

    const ruleColumnIndex = groupColumns.findIndex(
        (column) => column.columnKey === ruleColumnKey
    );

    const groupRowsLimit =
        userSettings.groupRowsLimit ?? DEFAULT_GROUP_ROWS_LIMIT;

    const onGroupRowsLimitChange = useCallback(
        (value) => {
            updateSettings((prev) => ({...prev, groupRowsLimit: value}));
        },
        [updateSettings]
    );

    const filterParams = useMemo(() => buildFilter(filters), [filters]);

    const groupAggregations = useMemo(
        () => buildAggregations(flattenedHeaders),
        [flattenedHeaders]
    );

    // rozkład i podgląd grup w modalu poziomu - ten sam wynik, filtr i poziomy co tabela
    const groupDistribution = useMemo(
        () =>
            ruleColumnIndex === -1
                ? undefined
                : {
                      resultId: id,
                      filter: filterParams,
                      groupFields: apiGroupFields,
                      parentColumn: groupColumns[ruleColumnIndex - 1],
                      // etykiety grup poziomu wyżej z jego reguły (wpis użytkownika, nie wartości z danych)
                      parentRule: apiGroupFields[ruleColumnIndex - 1]?.Buckets
                          ? groupRules[
                                groupColumns[ruleColumnIndex - 1].columnKey
                            ]
                          : undefined,
                      childColumn: groupColumns[ruleColumnIndex + 1],
                  },
        [
            ruleColumnIndex,
            id,
            filterParams,
            apiGroupFields,
            groupColumns,
            groupRules,
        ]
    );

    const groups = useAsyncTableGroups({
        resultId: id,
        groupFields: apiGroupFields,
        filter: filterParams,
        sortFields: sort,
        aggregations: groupAggregations,
        groupSorts: levelGroupSorts,
        groupsLimit: limit,
        rowsLimit: groupRowsLimit,
        // grupowanie dopiero po wczytaniu zapisanych widoków tabeli
        ready: views.ready,
    });

    // grupy w drodze - także zanim ruszy pierwsze zapytanie: po przejściu z płaskiej tabeli hook nie ma jeszcze
    // odpowiedzi ani ładowania, puste body skracało tabelę i przewijany kontener (np. modal) wracał na górę
    const groupsPending =
        isGrouped &&
        (groups.top.loading || (!groups.top.response && !groups.top.error));

    // główna paginacja w trybie grupowania liczy tylko grupy 1. poziomu
    const groupsSummary = useMemo(() => {
        if (!isGrouped) return undefined;
        const count = groups.top.response?.AllGroupsCount ?? 0;
        const from = count === 0 ? 0 : (groups.top.page - 1) * limit + 1;
        const to = Math.min(groups.top.page * limit, count);
        return t("firstLevelGroupsRange", {from, to, total: count});
    }, [isGrouped, groups.top, limit, t]);

    // paginacja w trybie grupowania przechodzi po stronach grup poziomu 0
    const setGroupsPage = useCallback(
        (value) =>
            groups.setTopPage(
                typeof value === "function" ? value(groups.top.page) : value
            ),
        [groups]
    );

    // w trybie grupowania wiersze pobiera useAsyncTableGroups - plaska tabela nic nie pobiera
    useEffect(() => {
        if (!id || isGrouped) return;
        setData(new Map());
        if (page === 1) {
            getPageData(1, limit, sort, filters);
        } else {
            setPage(1);
        }
    }, [limit, sort, filters, isGrouped]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isGrouped || data.has(page)) return;

        getPageData(page, limit, sort, filters);
    }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    if (error) {
        return (
            <InfoBox boxColor="error" outline>
                <i className="fa-solid fa-exclamation-circle me-2" />
                <span>
                    {t("errorOccuredWhenFetchingDataContactAdministrator")}
                </span>
            </InfoBox>
        );
    }
    // zapisane widoki jeszcze się wczytują - bez tego najpierw mignęłyby domyślne kolumny
    if (!views.ready) {
        return (
            <div className="async-table async-table-views-loading">
                <LoadingComponent isLoading bgInherit />
            </div>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
            autoScroll={autoScroll}>
            <div className="async-table">
                <AsyncTableActionBar
                    onSettingsClick={onSettingsClick}
                    groupColumns={groupColumns}
                    groupRules={groupRules}
                    onRemoveGroup={onRemoveGroup}
                    onConfigureGroup={setRuleColumnKey}
                    groupDroppableId={GROUP_DROPZONE_ID}
                    groupLevels={groupColumns.length}
                    onCollapseAll={groups.collapseAll}
                />
                <div className="async-table-container">
                    <table ref={table} key={tableKey}>
                        <AsyncTableColGroups
                            headers={visibleHeaders}
                            shouldIndex={showIndex}
                            table={table}
                        />
                        <AsyncTableHeaders
                            headers={visibleHeaders}
                            shouldIndex={showIndex}
                            setSort={setSort}
                            sort={sort}
                            filters={filters}
                            setFilters={setFilters}
                            id={id}
                            // w trybie grupowania także ładowanie grup i wierszy w grupach
                            disabled={loading || (isGrouped && groups.loading)}
                            groupSorts={groupSorts}
                            onGroupSortChange={onGroupSortChange}
                        />
                        {(loading || groupsPending) && (
                            <AsyncTableLoader
                                limit={limit}
                                allItems={allItems}
                                page={page}
                                headersCount={
                                    showIndex
                                        ? flattenedHeaders.length + 1
                                        : flattenedHeaders.length
                                }
                            />
                        )}
                        {!loading && !isGrouped && (
                            <AsyncTableBody
                                rows={dataToRender}
                                headers={flattenedHeaders}
                                shouldIndex={showIndex}
                                offset={(page - 1) * limit}
                            />
                        )}
                        {!loading && isGrouped && !groupsPending && (
                            <AsyncTableGroupedBody
                                groups={groups}
                                headers={flattenedHeaders}
                                shouldIndex={showIndex}
                                aggregations={groupAggregations}
                                groupHeaders={groupHeaders}
                                groupFields={groupFields}
                                groupRules={groupRulesByField}
                                groupDateUnits={groupDateUnitsByField}
                                rowsLimit={groupRowsLimit}
                                onRowsLimitChange={onGroupRowsLimitChange}
                            />
                        )}
                        {/* w trybie grupowania podsumowanie to wiersz "Razem" na gorze */}
                        {!isGrouped && (
                            <AsyncTableFooter
                                headers={flattenedHeaders}
                                shouldIndex={showIndex}
                                rows={dataToRender}
                                id={id}
                            />
                        )}
                    </table>
                </div>
                <AsyncTablePagination
                    limit={limit}
                    setLimit={setLimit}
                    maxItems={
                        isGrouped
                            ? (groups.top.response?.AllGroupsCount ?? 0)
                            : allItems
                    }
                    page={isGrouped ? groups.top.page : page}
                    setPage={isGrouped ? setGroupsPage : setPage}
                    disabled={loading}
                    summary={groupsSummary}
                />
                <AsyncTableSettings
                    show={showSettings}
                    onHide={onSettingsHide}
                    views={views}
                    settings={userSettings}
                    headers={orderedHeaders}
                    hiddenKeys={hiddenKeys}
                    setHiddenKeys={setHiddenKeys}
                    onMoveColumn={onMoveColumn}
                    onAddColumnGroup={onAddColumnGroup}
                    onRenameColumnGroup={onRenameColumnGroup}
                    onRemoveColumnGroup={onRemoveColumnGroup}
                    aggregates={aggregates}
                    onAggregateChange={onAggregateChange}
                    onRestoreDefault={onRestoreDefault}
                    groupColumns={groupColumns}
                    onRemoveGroup={onRemoveGroup}
                    onGroupBy={onGroupBy}
                    onMoveGroupLevel={onMoveGroupLevel}
                    groupRowsLimit={groupRowsLimit}
                    onGroupRowsLimitChange={onGroupRowsLimitChange}
                />
                {ruleColumnIndex !== -1 && (
                    <AsyncTableGroupRuleModal
                        column={groupColumns[ruleColumnIndex]}
                        rule={groupRules[ruleColumnKey]}
                        sort={groupSorts[ruleColumnKey]}
                        dateUnit={groupDateUnits[ruleColumnKey]}
                        level={ruleColumnIndex + 1}
                        levelsCount={groupColumns.length}
                        onApply={onApplyGroupRule}
                        onClose={onCloseGroupRule}
                        distribution={groupDistribution}
                    />
                )}
            </div>
            {/* portal - a transformed ancestor (e.g. dashboard grid item) would offset the fixed overlay;
                zIndex above bootstrap modals (1055, views modal 1075) */}
            {createPortal(
                <DragOverlay dropAnimation={null} zIndex={1100}>
                    {draggedColumn && (
                        <span className="async-table-drag-overlay">
                            <i className="fa-solid fa-layer-group" />
                            {draggedColumn.name}
                        </span>
                    )}
                </DragOverlay>,
                document.body
            )}
        </DndContext>
    );
}
